/* __next_internal_client_entry_do_not_use__ HeaderProvider,useHeader auto */ import { jsx as _jsx } from "react/jsx-runtime";
// Global
import { createContext, useCallback, useContext, useState } from 'react';
import { usePathname } from 'next/navigation';
const HeaderContext = /*#__PURE__*/ createContext(undefined);
export const HeaderProvider = ({ children })=>{
    /*
   * The path the active-state helpers below match against. Deliberately the pathname alone.
   *
   * It used to carry the query string from `useSearchParams`, which is empty during server
   * rendering and populated on the client — so the server HTML and the first client render
   * disagreed, and the header had to withhold the active state behind an `isClient` flag and
   * paint it a beat after load. `usePathname` returns the same value in both places, so the
   * active state can now ship in the HTML. It also fixes a real bug: nav hrefs carry no query
   * string, so on `/personal?utm=x` nothing used to match at all.
   */ const asPath = usePathname() || '/';
    // Desktop states
    const [isDesktopDropdownOpen, setDesktopDropdownOpen] = useState(null);
    const [isDesktopLanguageSelectorOpen, setDesktopLanguageSelectorOpen] = useState(false);
    // Mobile states
    const [isMobile, setIsMobile] = useState(false);
    const [isMobileDropdownOpen, setMobileDropdownOpen] = useState(null);
    const [isMobileLanguageSelectorOpen, setMobileLanguageSelectorOpen] = useState(false);
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    // Shared states
    const [isOverlayVisible, setIsOverlayVisible] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    // Desktop actions
    const closeDesktopMenus = useCallback(()=>{
        setDesktopDropdownOpen(null);
        setDesktopLanguageSelectorOpen(false);
        setShowSearch(false);
        const shouldShowOverlay = isMobile ? isMobileMenuOpen || isMobileDropdownOpen !== null || showSearch || isMobileLanguageSelectorOpen : false;
        setIsOverlayVisible(shouldShowOverlay);
    }, [
        isMobile,
        isMobileMenuOpen,
        isMobileDropdownOpen,
        showSearch,
        isMobileLanguageSelectorOpen
    ]);
    const setDesktopDropdownOpenWithOverlay = useCallback((index)=>{
        if (index === isDesktopDropdownOpen) {
            setDesktopDropdownOpen(null);
        } else {
            setDesktopDropdownOpen(index);
        }
    }, [
        isDesktopDropdownOpen
    ]);
    const setDesktopLanguageSelectorOpenWithOverlay = useCallback((isOpen)=>{
        setDesktopLanguageSelectorOpen(isOpen);
        if (isOpen) {
            setDesktopDropdownOpen(null);
            setShowSearch(false);
        }
    }, []);
    // Mobile actions
    const closeMobileMenu = useCallback(()=>{
        setMobileMenuOpen(false);
        setMobileDropdownOpen(null);
        setMobileLanguageSelectorOpen(false);
        setShowSearch(false);
        const shouldShowOverlay = false;
        setIsOverlayVisible(shouldShowOverlay);
    }, []);
    const setMobileDropdownOpenWithOverlay = useCallback((index)=>{
        if (index === isMobileDropdownOpen) {
            setMobileDropdownOpen(null);
        } else {
            setMobileDropdownOpen(index);
        }
    }, [
        isMobileDropdownOpen
    ]);
    const setMobileLanguageSelectorOpenWithOverlay = useCallback((isOpen)=>{
        setMobileLanguageSelectorOpen(isOpen);
        if (isOpen) {
            setMobileDropdownOpen(null);
            setShowSearch(false);
        }
    }, []);
    // Shared actions
    const handleOverlayChange = useCallback(()=>{
        const shouldShowOverlay = isMobile ? isMobileMenuOpen || isMobileDropdownOpen !== null || showSearch || isMobileLanguageSelectorOpen : isDesktopDropdownOpen !== null || showSearch || isDesktopLanguageSelectorOpen;
        setIsOverlayVisible(shouldShowOverlay);
    }, [
        isMobile,
        isMobileMenuOpen,
        isMobileDropdownOpen,
        showSearch,
        isDesktopDropdownOpen,
        isMobileLanguageSelectorOpen,
        isDesktopLanguageSelectorOpen
    ]);
    const handleRouteChange = useCallback(()=>{
        if (isMobile) {
            closeMobileMenu();
        } else {
            closeDesktopMenus();
        }
    }, [
        isMobile,
        closeMobileMenu,
        closeDesktopMenus
    ]);
    // Active state helpers - NOT memoized so they recalculate with fresh data
    /*
   * The header arrives in one of two shapes: the flat layout-service payload
   * (`fields.link.value.href`) or the Edge GraphQL one (`link.jsonValue.value.href`, lists
   * under `targetItems`). These read either, because the helpers below used to look only at
   * `fields`, found nothing under GraphQL, and fell through to a prefix match — which is why
   * /personal-injury-claims lit up "Personal".
   */ const hrefOf = (field)=>{
        const f = field;
        return f?.value?.href ?? f?.jsonValue?.value?.href;
    };
    const listOf = (field)=>{
        const f = field;
        return Array.isArray(field) ? field : f?.targetItems ?? [];
    };
    /**
   * True when `path` is the link itself or sits underneath it, matching whole segments only:
   * /personal matches /personal and /personal/conveyancing, never /personal-injury-claims.
   */ const isUnder = (path, link)=>{
        const a = path.toLowerCase().replace(/\/+$/, '');
        const b = link.toLowerCase().replace(/\/+$/, '');
        return !!b && (a === b || a.startsWith(b + '/'));
    };
    const isTier1Active = (navigationLinks, megaMenuList, isList)=>{
        if (isList && megaMenuList) {
            if (asPath === '/') return false;
            // If the main nav link is external, don't mark as active for internal pages
            const isExternalLink = navigationLinks?.startsWith('http://') || navigationLinks?.startsWith('https://');
            if (isExternalLink) {
                return false;
            }
            // Get all tier 3 links from mega menu (handle both flat and column group structures)
            const tier3Links = [];
            megaMenuList.forEach((category)=>{
                const cat = category;
                const megaMenuLinks = listOf(cat.megaMenuLinks ?? cat.fields?.megaMenuLinks);
                megaMenuLinks.forEach((entry)=>{
                    const linkItem = entry;
                    // A column group holds its links one level down; a flat entry is the link itself.
                    const nested = listOf(linkItem.megaMenuCategory ?? linkItem.fields?.megaMenuCategory);
                    if (nested.length) {
                        nested.forEach((child)=>{
                            const catLink = child;
                            const href = hrefOf(catLink.link ?? catLink.fields?.link);
                            if (href) tier3Links.push(href);
                        });
                    } else {
                        const href = hrefOf(linkItem.link ?? linkItem.fields?.link);
                        if (href) tier3Links.push(href);
                    }
                });
            });
            // Get all tier 2 category links (megaMenuCTA)
            const tier2Links = megaMenuList.map((category)=>{
                const cat = category;
                return hrefOf(cat.megaMenuCTA ?? cat.fields?.megaMenuCTA);
            }).filter((href)=>!!href);
            // Combine all links
            const allLinks = [
                ...tier3Links,
                ...tier2Links
            ];
            // Check if data is loaded (not all placeholder '/' links)
            const hasRealData = allLinks.some((href)=>href !== '/');
            const normalizedAsPath = asPath.toLowerCase();
            // If data isn't loaded yet, check using URL path matching
            if (!hasRealData && navigationLinks && navigationLinks !== '/') {
                /*
         * Whole-segment match, not a raw prefix: a raw one made /personal-injury-claims look
         * like a page under /personal.
         */ return isUnder(asPath, navigationLinks);
            }
            /*
       * Descendants count, not just exact hits: a child being active makes its parent
       * active, so /personal/employment-law/dismissal-redundancy lights up Personal even
       * though only /personal/employment-law is in the list. Whole-segment, so
       * /personal-injury-claims still never matches /personal.
       */ return allLinks.some((href)=>isUnder(normalizedAsPath, href)) || (navigationLinks ? isUnder(normalizedAsPath, navigationLinks) : false);
        } else {
            return navigationLinks ? asPath.toLowerCase() === navigationLinks.toLowerCase() : false;
        }
    };
    const isTier2Active = (category)=>{
        if (asPath === '/') return false;
        // Only check the tier 2 category link itself, not tier 3 links under it
        const cat = category;
        const categoryLink = hrefOf(cat.megaMenuCTA ?? cat.fields?.megaMenuCTA);
        return categoryLink ? categoryLink.toLowerCase() === asPath.toLowerCase() : false;
    };
    /**
   * How well a tier 2 category matches the current page. 0 means no match; a higher score
   * is a better one. The length of the matched link breaks ties between nested links, and
   * the category's own link outranks any tier 3 link inside it.
   *
   * That ranking is what settles a page that is listed in two categories:
   * /personal/guides/conveyancing-guide sits in Guides and is also linked from
   * Conveyancing, but only Guides owns the path, so Guides wins. Taking the first match
   * instead picked whichever category happened to come first in the list.
   */ const tier2MatchScore = (category)=>{
        if (asPath === '/') return 0;
        const cat = category;
        // The category's own page: an outright owner, so it beats every tier 3 link.
        const categoryLink = hrefOf(cat.megaMenuCTA ?? cat.fields?.megaMenuCTA);
        if (categoryLink && isUnder(asPath, categoryLink)) return 1000 + categoryLink.length;
        let best = 0;
        listOf(cat.megaMenuLinks ?? cat.fields?.megaMenuLinks).forEach((entry)=>{
            const linkItem = entry;
            // A column group holds its links one level down; a flat entry is the link itself.
            const nested = listOf(linkItem.megaMenuCategory ?? linkItem.fields?.megaMenuCategory);
            const hrefs = nested.length ? nested.map((child)=>{
                const childLink = child;
                return hrefOf(childLink.link ?? childLink.fields?.link);
            }) : [
                hrefOf(linkItem.link ?? linkItem.fields?.link)
            ];
            hrefs.forEach((href)=>{
                if (href && isUnder(asPath, href)) best = Math.max(best, href.length);
            });
        });
        return best;
    };
    /**
   * True when the current page is the tier 2 category itself or anything underneath it —
   * its own link, one of its tier 3 links, or a page below either. `isTier2Active` above
   * stays an exact match on purpose, because it marks the category's "Overview" link,
   * which points at the category page alone.
   */ const isTier2SectionActive = (category)=>tier2MatchScore(category) > 0;
    /**
   * Index of the one tier 2 category that owns the current page, or -1. Use this rather
   * than the first `isTier2SectionActive` hit, so a page linked from several categories
   * marks only the category it actually lives under.
   */ const getActiveTier2Index = (megaMenuList)=>{
        if (!megaMenuList?.length) return -1;
        let bestIndex = -1;
        let bestScore = 0;
        megaMenuList.forEach((category, index)=>{
            const score = tier2MatchScore(category);
            if (score > bestScore) {
                bestScore = score;
                bestIndex = index;
            }
        });
        return bestIndex;
    };
    const isTier3Active = (href)=>{
        if (!href || asPath === '/') return false;
        return href.toLowerCase() === asPath.toLowerCase();
    };
    const value = {
        // Desktop states
        isDesktopDropdownOpen,
        isDesktopLanguageSelectorOpen,
        showSearch,
        // Mobile states
        isMobile,
        isMobileDropdownOpen,
        isMobileLanguageSelectorOpen,
        isMobileMenuOpen,
        // Shared states
        isOverlayVisible,
        // Desktop actions
        setDesktopDropdownOpen: setDesktopDropdownOpenWithOverlay,
        setDesktopLanguageSelectorOpen: setDesktopLanguageSelectorOpenWithOverlay,
        closeDesktopMenus,
        // Mobile actions
        setIsMobile,
        setMobileDropdownOpen: setMobileDropdownOpenWithOverlay,
        setMobileLanguageSelectorOpen: setMobileLanguageSelectorOpenWithOverlay,
        setMobileMenuOpen,
        closeMobileMenu,
        // Shared actions
        setShowSearch,
        handleOverlayChange,
        handleRouteChange,
        // Active state helpers
        isTier1Active,
        isTier2Active,
        isTier2SectionActive,
        getActiveTier2Index,
        isTier3Active,
        asPath
    };
    return /*#__PURE__*/ _jsx(HeaderContext.Provider, {
        value: value,
        children: children
    });
};
// Custom hook to use the context
export const useHeader = ()=>{
    const context = useContext(HeaderContext);
    if (context === undefined) {
        throw new Error('useHeader must be used within a HeaderProvider');
    }
    return context;
};
